
import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { PropertyComparisonProvider } from '@/contexts/PropertyComparisonContext';
import Index from '@/pages/Index';
import SignUp from '@/pages/SignUp';
import SignIn from '@/pages/SignIn';
import PasswordReset from '@/pages/PasswordReset';
import EmailVerification from '@/pages/EmailVerification';
import ListingDetail from '@/pages/ListingDetail';
import SearchListings from '@/pages/SearchListings';
import Features from '@/pages/Features';
import AboutUs from '@/pages/AboutUs';
import Contact from '@/pages/Contact';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Compare from '@/pages/Compare';
import Dashboard from '@/pages/Dashboard';
import Account from '@/pages/Account';
import ListProperty from '@/pages/ListProperty';
import MyListings from '@/pages/MyListings';
import EditListing from '@/pages/EditListing';
import Wishlist from '@/pages/Wishlist';
import AdminLayout from '@/layouts/AdminLayout';
import AdminDashboard from '@/components/admin/AdminDashboard';
import UserManagement from '@/pages/admin/UserManagement';
import KYCManagement from '@/pages/admin/KYCManagement';
import ListingsManagement from '@/pages/admin/ListingsManagement';
import ListingsApproval from '@/pages/admin/ListingsApproval';
import UniversitiesManagement from '@/pages/admin/UniversitiesManagement';
import AdminSettings from '@/pages/admin/AdminSettings';
import SecurityDashboard from '@/pages/admin/SecurityDashboard';
import NotFound from '@/pages/NotFound';
import AuthGuard from '@/components/auth/AuthGuard';
import ProtectedRoute from '@/components/ProtectedRoute';
import AuthRoute from '@/components/AuthRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from 'sonner';

// Safari-specific debugging
console.log('🦎 Safari App.tsx loading...');
console.log('🦎 User Agent:', navigator.userAgent);
console.log('🦎 Is Safari:', navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        console.error('🚨 Query failed:', error);
        return failureCount < 2; // Reduced retries for Safari
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App: React.FC = () => {
  console.log('🦎 Safari App component rendering...');
  
  // Safari compatibility check
  React.useEffect(() => {
    console.log('🦎 Safari App useEffect running...');
    
    if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
      console.log('🦎 Safari detected - applying compatibility fixes');
      
      // Check for common Safari issues
      if (!window.fetch) {
        console.error('🚨 Safari: fetch API not available');
      }
      
      if (!window.Promise) {
        console.error('🚨 Safari: Promise not available');
      }
      
      // Log Safari version
      const safariVersion = navigator.userAgent.match(/Version\/([0-9.]+)/);
      if (safariVersion) {
        console.log('🦎 Safari version:', safariVersion[1]);
      }
    }
  }, []);

  try {
    return (
      <ErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <LanguageProvider>
                <PropertyComparisonProvider>
                  <div className="min-h-screen bg-background text-foreground">
                    <div className="theme-gradient">
                      <Router>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/signup" element={<AuthRoute><SignUp /></AuthRoute>} />
                          <Route path="/signin" element={<AuthRoute><SignIn /></AuthRoute>} />
                          <Route path="/password-reset" element={<AuthRoute><PasswordReset /></AuthRoute>} />
                          <Route path="/email-verification" element={<EmailVerification />} />
                          <Route path="/listing/:id" element={<ProtectedRoute actionType="view_listing"><ListingDetail /></ProtectedRoute>} />
                          <Route path="/search" element={<SearchListings />} />
                          <Route path="/features" element={<Features />} />
                          <Route path="/about" element={<AboutUs />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/terms" element={<Terms />} />
                          <Route path="/compare" element={<Compare />} />
                          
                          {/* Protected routes that require KYC */}
                          <Route path="/dashboard" element={<ProtectedRoute requireKYC={true}><Dashboard /></ProtectedRoute>} />
                          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                          <Route path="/list-property" element={<ProtectedRoute requireKYC={true} actionType="list_property"><ListProperty /></ProtectedRoute>} />
                          <Route path="/my-listings" element={<ProtectedRoute requireKYC={true}><MyListings /></ProtectedRoute>} />
                          <Route path="/edit-listing/:id" element={<ProtectedRoute requireKYC={true}><EditListing /></ProtectedRoute>} />
                          <Route path="/wishlist" element={<ProtectedRoute requireKYC={true}><Wishlist /></ProtectedRoute>} />
                          
                          {/* Admin routes */}
                          <Route path="/admin" element={
                            <ProtectedRoute>
                              <AuthGuard requiredRole={["admin"]}>
                                <AdminLayout />
                              </AuthGuard>
                            </ProtectedRoute>
                          }>
                            <Route index element={<AdminDashboard />} />
                            <Route path="users" element={<UserManagement />} />
                            <Route path="kyc" element={<KYCManagement />} />
                            <Route path="listings" element={<ListingsApproval />} />
                            <Route path="listings-management" element={<ListingsManagement />} />
                            <Route path="universities" element={<UniversitiesManagement />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="security" element={<SecurityDashboard />} />
                          </Route>
                          
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        
                        <Toaster />
                        <SonnerToaster position="bottom-right" />
                      </Router>
                    </div>
                  </div>
                </PropertyComparisonProvider>
              </LanguageProvider>
            </AuthProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    );
  } catch (error) {
    console.error('🚨 Safari App component error:', error);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Loading Error</h1>
          <p className="text-muted-foreground mb-4">Please refresh the page</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-primary text-primary-foreground px-4 py-2 rounded"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }
};

export default App;
